import { Component, OnInit, Input, Output, EventEmitter, forwardRef }           from '@angular/core';
import { Location }                   from '@angular/common';
import { FormBuilder, FormGroup, FormArray, Validators, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import { Router }                     from '@angular/router';

import { JourneysService }            from './../../journeys.service';
import { PermissionsService }         from '../../../../shared/services/permissions.service';

import { JourneyDetail }              from './../../../../models/journey/journey-detail';
import { JourneyStep }                from './../../../../models/journey-editor/journey-step';
import { Tag }                        from './../../../../models/journey-editor/tag';
import { Resource }                   from './../../../../models/journey-editor/resource';
import { Program }                    from './../../../../models/journey-editor/program';
import { CommunityChannel }           from './../../../../models/journey-editor/community-channel';

@Component({
  selector: 'app-journey-content',
  templateUrl: './journey-content.component.html',
  styleUrls: ['./journey-content.component.less'],
  providers: [
      JourneysService, 
      PermissionsService,
      {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => JourneyContentComponent),
        multi: true
      }
    ]
})

export class JourneyContentComponent implements OnInit {
  @Input() journey: JourneyDetail;
  @Output() update: EventEmitter<JourneyDetail> = new EventEmitter<JourneyDetail>();
  @Input() savingMetadata: Boolean;
  public steps: JourneyStep[];
  public stepErrors: string[];
  public journeyLoaded: boolean;
  public savingEdits: boolean;
  public content: FormGroup;
  public stepContent: FormGroup;;
  public editableStep: number;
  public communityTags: Tag[];
  public resourceTags: Tag[];
  public programs: Program[];
  public resources: Resource[];
  public channels: CommunityChannel[];
  public selectedResources: Resource[];
  public stepToDelete: JourneyStep;
  public addNewStep: boolean;
  public stepTitles: {
    webinar: string, 
    program: string, 
    resource_list: string, 
    honor_system: string, 
    community_posts: string
  }

  constructor(
    private journeysService: JourneysService,
    private permissionsService: PermissionsService,
    private router: Router,
    private _fb: FormBuilder,
  ) { }

  ngOnInit(): void {
    this.permissionsService.checkPermission('editJourneys').subscribe(canEditJourneys => {
      if (!canEditJourneys) {
        this.router.navigate(["dashboard", "journeys"]);
      }
    });
    this.selectedResources = [];
    this.stepErrors = [];
    this.journeyLoaded = false;
    this.addNewStep = false;
    this.editableStep = -1;
    this.stepTitles = {
      webinar: "Autocomplete on Date",
      program: "Program",
      resource_list: "Resources",
      community_posts: "Community",
      honor_system: "Click to Complete"
    }

    this.journeysService
      .getJourneySteps(this.journey.id)
      .subscribe(steps => {
        this.steps = steps;
        this.journeyLoaded = true;
      });

    this.journeysService
      .getJourneyStepOptions()
      .subscribe(options => {
        this.communityTags = options.community_tags;
        this.resourceTags = options.resource_tags;
        this.programs = options.programs;
        this.resources = options.resources;
        this.channels = options.channels;
      });

    this.stepContent = this.getStepContentTemplate();
  }

  public getStepContentTemplate(stepType=null): FormGroup {
    let formData = {
      label: [null],
      custom_description: [null],
      custom_title: [null],
      sort_order: [this.steps ? this.steps.length + 1 : 0],
      notification_date: [null],
      media: [null],
    }
    switch (stepType) {
      case "program":
        formData["program"] = [null, Validators.required];
        break;
      case "resource_list":
        formData["count"] = [null];
        formData["tag"] = [null];
        formData["channel"] = [null];
        break;
      case "webinar":
        formData["date"] = [null, Validators.required];
        formData["link"] = [null];
        formData["custom_button_text"] = [null];
        break;
      case "honor_system":
        formData["custom_button_text"] = [null];
        break;
      case "community_posts":
        formData["count"] = [null, Validators.required];
        formData["channel"] = [null];
        formData["tag"]=[null];
        break;
    }
    return this._fb.group(formData);
  }

  public validateStepEdits(): void {
    this.stepErrors = [];
    for (const key of Object.keys(this.stepContent.controls)) {
      if (this.stepContent.controls[key].errors) {
        let formattedKey = key.charAt(0).toUpperCase() + key.slice(1);
        this.stepErrors.push(`${formattedKey} is required.`);
      }
    };
    this.stepErrors = this.stepErrors.slice(); // Trigger UI update
  }

  public saveStepEdits(): void {
    this.validateStepEdits()
    if (this.stepErrors.length) {
      return;
    }
    this.savingEdits = true;
    let value;
    let step = JSON.parse(JSON.stringify(this.steps[this.editableStep]))
    for (let key in this.stepContent.controls) {
      value = this.stepContent.controls[key].value;
      if (key === 'media') {
        if (value == null || value === '') {
          step.image = null;
          step.media_video_id = null;
        } else if (typeof value === 'string') {
          // If we already have an image/video, keep it.
        } else {
          step.image = null;
          step.media_video_id = null;
          if (value.videoId) {
            step.media_video_id = value.videoId;
          } else if (value.key) {
            step.image = value.key;
          }
        }
      } else {
        step[key] = value;
      }
    }
    // Handle multiselect case for Resources 
    if (step.type == "resource_list") {
      step.resources = this.selectedResources.map((r)=>r.id);
    } 
    this.journeysService
    .submitJourneyStepEdits(this.journey.id, step)
    .subscribe(
      (updatedStep) => {
        this.steps[this.editableStep] = updatedStep;
        this.editableStep = -1;
        this.savingEdits = false;
        this.selectedResources = [];
      },
      (error) => {
        this.stepErrors.push("Something went wrong while saving your edits. Please try again.");
        this.savingEdits = false;
      }
    );
  }

  public deleteStep(step: JourneyStep): void {
    this.journeysService
    .deleteStep(this.journey.id, step.id)
    .subscribe(() => {
      this.steps = this.steps.filter((s) => s.id !== step.id)
      this.stepToDelete = null;
    });
  }

  public moveStep(oldIndex: number, newIndex: number): void {
    if (newIndex < 0 || (oldIndex < newIndex && oldIndex == this.steps.length - 1)) {
      return;
    }
    let oldStep = this.steps[newIndex];
    this.steps[newIndex] = this.steps[oldIndex];
    this.steps[oldIndex] = oldStep;
    this.steps = this.steps.slice(); // Trigger update in UI

    this.journeysService
    .reorderJourneySteps(this.journey.id, this.steps)
    .subscribe((steps) => {
      this.steps = steps;
    });
  }

  public addResource(resource: number): void {
    if (resource) {
      let selectedResource = this.resources.find((r)=>r.id == resource);
      this.selectedResources.push(selectedResource);
      this.selectedResources = this.selectedResources.slice(); // Trigger update in UI
    };
  }

  public removeResource(resource: Resource): void {
    this.selectedResources = this.selectedResources.filter((r)=> r != resource);
    this.selectedResources = this.selectedResources.slice();
  }

  public editNewStep(stepType: string): void {
    let step = new JourneyStep;
    step.type = stepType;
    this.stepContent = this.getStepContentTemplate(step.type)
    this.steps.push(step);
    this.editableStep = this.steps.length - 1;
    this.addNewStep = false;
  }

  public getStepContent(step: JourneyStep): string {
    // Return a sanitised version without HTML tags
    // and ellipsize to 200 chars
    const TRUNCATE = 200;
    const stepContent = this.getRawStepContent(step);
    const div = document.createElement("div");
    div.innerHTML = stepContent;
    let cleanContent = div.innerText;
    if (cleanContent.length > TRUNCATE) {
      cleanContent = cleanContent.substring(0, TRUNCATE - 1) + '…';
    }
    return cleanContent;
  }

  public getRawStepContent(step: JourneyStep): string {
    switch(step.type) {
      case 'program':
        return step.program ? step.program.title : "No Program selected";
      default:
        return step.custom_description || step.default_description;
    }
  }

  public setEditableStep(index: number): void {
    let newVal;
    // Set form values for selected step
    if (index > -1) {
      let step = this.steps[index];
      this.stepContent = this.getStepContentTemplate(step.type)
      this.editableStep = index;
      // Handle multiselect case with Resource
      if ('resources' in step) {
        this.selectedResources = step.resources;
      }
      for (let key in step) {
        if (key === 'image' && step.image) {
          this.stepContent.controls['media'].setValue(step.image);
        } else if (key === 'media_video_url' && step.media_video_url) {
          this.stepContent.controls['media'].setValue(step.media_video_url);
        } else if (Object.keys(this.stepContent.controls).indexOf(key) > -1) {
          if (step[key] && typeof step[key] === 'object') {
            newVal = step[key].id;
          } else {
            newVal = step[key];
          }
          this.stepContent.controls[key].setValue(newVal);        
        }
      }
    } else {
      // Reset form values
      this.editableStep = index;
      this.stepErrors = [];
      this.stepContent = this.getStepContentTemplate()
      // If a step was added, but not saved, remove it steps
      if (this.steps.length && !this.steps[this.steps.length - 1].id) {
        this.steps.pop();
      }
      this.selectedResources = [];
    }
  }
}



// WEBPACK FOOTER //
// /home/runner/work/nomadic-admin/nomadic-admin/nomadic-admin/src/app/dashboard/journeys/journey/journey-content/journey-content.component.ts